import { useCallback, useEffect, useState } from 'react'
import { isToggleShortcut, parseSidebarPrefs, SIDEBAR_PREFS_KEY, toggleFolded, type SidebarPrefs } from '@/lib/sidebarPrefs'

function read(): SidebarPrefs {
  try {
    return parseSidebarPrefs(localStorage.getItem(SIDEBAR_PREFS_KEY))
  } catch {
    return parseSidebarPrefs(null)
  }
}

/**
 * The sidebar's rail / folded sections, remembered per browser and followed
 * across tabs; `[` toggles the rail.
 */
export function useSidebarPrefs() {
  const [prefs, setPrefs] = useState<SidebarPrefs>(read)

  const save = useCallback((next: SidebarPrefs) => {
    setPrefs(next)
    try {
      localStorage.setItem(SIDEBAR_PREFS_KEY, JSON.stringify(next))
    } catch {
      // Private mode / quota: the layout just isn't remembered.
    }
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIDEBAR_PREFS_KEY) setPrefs(read())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const toggleCollapsed = useCallback(() => save({ ...read(), collapsed: !read().collapsed }), [save])
  const toggle = useCallback((key: string) => save(toggleFolded(read(), key)), [save])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isToggleShortcut(e)) return
      e.preventDefault()
      toggleCollapsed()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleCollapsed])

  return { prefs, toggleCollapsed, toggle, isFolded: (key: string) => prefs.folded.includes(key) }
}
